/**
 * absenceQuotaFormatter — ce que l'encadré « Absences » annonce d'une absence qu'on s'apprête à
 * signaler, et ce que l'étape des créneaux en déduit.
 */
import { type UIAbsenceNotRecoveredReason, type UIAbsenceQuota } from "../types"

/**
 * Ce que l'absence fait au quota, une fois confirmée.
 *
 * - `counted` : elle se rattrape, donc elle prend un crédit ; `remaining` dit ce qui restera alors
 *   sur la période (le plafond moins les crédits utilisés après elle, jamais sous 0). Sinon — moins
 *   de 2 h, quota atteint, séance déjà de rattrapage — le serveur la pose non rattrapable, elle ne
 *   prend aucun crédit, et l'encadré dit pourquoi (`reason`, Wajih 23-24/10).
 * - `reached` : le quota est atteint avant cette absence (le `isMaxReached` du serveur) — l'encadré
 *   passe en alerte.
 */
export function absenceQuotaAfterConfirm(quota: UIAbsenceQuota): {
    counted: boolean
    remaining: number
    reached: boolean
    reason?: UIAbsenceNotRecoveredReason
} {
    const counted = quota.recoverable
    const reached = quota.used >= quota.limit
    const used = counted ? quota.used + 1 : quota.used
    return {
        counted,
        remaining: Math.max(0, quota.limit - used),
        reached,
        reason: counted ? undefined : quota.reason,
    }
}

/**
 * Ce que l'étape des créneaux montre, d'après ce que la lecture a rendu.
 *
 * - `nothingToPick` : la lecture est revenue sans créneau ;
 * - `quotaSaysItAll` : aucun créneau, et l'encadré dit déjà que l'absence ne se rattrapera pas, et
 *   pourquoi — l'étape ne répète pas « aucun créneau » ;
 * - `refused` : le rattrapage est refusé pour de bon (le motif servi, ou l'encadré) ;
 * - `outcome` : ce que la ligne de contexte annonce — un créneau à choisir ici (`slot`), un
 *   rattrapage à planifier plus tard (`later`), aucun rattrapage (`refused`), ou seulement que la
 *   séance sera notée absente (`noted`) : l'encadré dit déjà pourquoi elle ne se rattrapera pas, la
 *   lecture est en cours, ou une erreur s'affiche sans créneau (Wajih, 23-24/10) ;
 * - `submitsWithoutSlot` : sans créneau à choisir, le bouton principal signale l'absence, dans les
 *   deux voix (Wajih, 23/10) — l'ancien formulaire, le même pour l'élève et le parent, laissait
 *   « Confirmer » actif.
 */
export function recoverySlotStepShape(args: {
    slotCount: number
    loading: boolean
    quota?: UIAbsenceQuota
    refusedForGood: boolean
    /** Une erreur est à l'écran (une lecture en échec, un envoi refusé). */
    errorShown?: boolean
}): {
    nothingToPick: boolean
    quotaSaysItAll: boolean
    refused: boolean
    outcome: "slot" | "later" | "refused" | "noted"
    submitsWithoutSlot: boolean
} {
    const nothingToPick = !args.loading && args.slotCount === 0
    const quotaSaysItAll = nothingToPick && args.quota != null && !absenceQuotaAfterConfirm(args.quota).counted
    const refused = args.refusedForGood || quotaSaysItAll
    const notYetKnown = args.loading || (nothingToPick && args.errorShown === true)
    const outcome = quotaSaysItAll
        ? "noted"
        : refused
          ? "refused"
          : notYetKnown
            ? "noted"
            : nothingToPick
              ? "later"
              : "slot"
    return { nothingToPick, quotaSaysItAll, refused, outcome, submitsWithoutSlot: nothingToPick }
}
